import { useTranslations } from "@zapier/i18n";
import { HydratedChatbotWithFolder } from "../useChatbotsList";

import {
  Button,
  ModalDialogBody,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogRoot,
  Spacer,
  Text,
} from "@zapier/design-system";

import { useDeleteChatbot } from "./useDeleteChatbot";
import { useEmitChatbotDeleteModalEvent } from "@zapier/shared/src/events";
import { FlexSpacer } from "@zapier/shared";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  chatbot: HydratedChatbotWithFolder | undefined;
  folderId?: string;
}

export function ChatbotDeleteModal({
  isOpen,
  onClose,
  chatbot,
  folderId,
}: Props) {
  const t = useTranslations("chatbot-delete-modal");

  const { deleteChatbot, isPending } = useDeleteChatbot(chatbot, folderId);
  const { emit } = useEmitChatbotDeleteModalEvent();

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      emit("cancel");
      onClose();
    }
  };

  if (!chatbot) {
    return;
  }

  const modalTitle = t("title", { name: chatbot.name ?? "" });
  const modalText = t("description");

  return (
    <ModalDialogRoot isOpen={isOpen} onOpenChange={handleOpenChange}>
      <>
        <ModalDialogHeader>{modalTitle}</ModalDialogHeader>

        <ModalDialogBody>
          <Text color="TextWeaker">{modalText}</Text>
          <Spacer height={12} />
        </ModalDialogBody>

        <ModalDialogFooter>
          <Button
            isDisabled={isPending}
            onPress={() => {
              emit("cancel");
              onClose();
            }}
            variant="tertiary"
          >
            {t("buttons.cancel")}
          </Button>

          <FlexSpacer />

          <Button
            isDisabled={isPending}
            isPending={isPending}
            onPress={() => {
              emit("confirm");
              deleteChatbot();
              onClose();
            }}
            variant="danger"
          >
            {t("buttons.delete")}
          </Button>
        </ModalDialogFooter>
      </>
    </ModalDialogRoot>
  );
}

// For ease of use during `dynamic` import
export default ChatbotDeleteModal;
